import { KText } from '@/ui-library/components';
import { Icon, Flex, Box, Tooltip } from '@chakra-ui/react';
import { memo, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { AiSupportAttributes } from '../AiScoringDrawer/AiSupportAttributes';
import {
  DragDropContext,
  Droppable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { AiScoreExplained } from '../AiScoreExplained';
import { AiScoringConstants } from '../aiScoringConstants';
import AiAttributeListItem, { AttributeRow } from './aiAttributeListItem';
import { type ProcessedAttribute } from '../utils/attributeValidation';

const {
  MAX_ATTRIBUTES,
  MIN_ATTRIBUTES_REQUIRED,
  NO_ATTRIBUTES_BANNER,
  INSUFFICIENT_ATTRIBUTES_BANNER,
} = AiScoringConstants;

interface AiAttributeListContainerProps {
  attributes: ProcessedAttribute[];
  isLoading: boolean;
  isValid: boolean;
  onAttributeChange: (id: number, value: string) => void;
  onAttributeBlur: (id: number) => void;
  onAttributeDelete: (id: number) => void;
  onAttributeAdd: () => { newAttributeId: number };
  onAttributeReorder: (sourceIndex: number, destinationIndex: number) => void;
  onExplainClick: () => void;
  onAttributeAutoFocus?: (id: number) => void;
  footerIsLoading?: boolean;
  generatedAttributes?: any[];
  /** Rendered below the attribute list and the Add attribute row
   * (e.g. the Scoring agent auto-reject bar). */
  afterListContent?: ReactNode;
}

export const AiAttributeListContainer = memo<AiAttributeListContainerProps>(
  ({
    attributes,
    isLoading,
    isValid: _isValid,
    onAttributeChange,
    onAttributeBlur,
    onAttributeDelete,
    onAttributeAdd,
    onAttributeReorder,
    onExplainClick: _onExplainClick,
    onAttributeAutoFocus,
    footerIsLoading: _footerIsLoading = false,
    generatedAttributes,
    afterListContent,
  }) => {
    // Refs for auto-focus and auto-scroll functionality
    const containerRef = useRef<HTMLDivElement>(null);
    const lastAttributeIdRef = useRef<number | null>(null);

    // Effect to handle auto-focus and auto-scroll when new attribute is added
    useEffect(() => {
      if (attributes.length > 0) {
        const lastAttribute = attributes[attributes.length - 1];

        // Check if this is a newly added attribute that needs focus
        // The condition checks: 1) it's new, 2) it's empty, 3) we just added it (ID matches our ref)
        if (
          lastAttribute.isNewAttribute &&
          lastAttribute.description.trim().length === 0 &&
          lastAttributeIdRef.current === lastAttribute.id
        ) {
          // Auto-focus after DOM updates
          setTimeout(() => {
            // First blur any currently focused input to trigger validation
            const currentlyFocused = document.activeElement as HTMLInputElement;
            if (
              currentlyFocused &&
              currentlyFocused.tagName === 'INPUT' &&
              currentlyFocused.dataset.attributeId
            ) {
              const focusedId = parseInt(currentlyFocused.dataset.attributeId);
              if (focusedId !== lastAttribute.id) {
                // Trigger blur event for proper validation before focusing new attribute
                currentlyFocused.blur();
                // Small delay to ensure blur processing completes
                setTimeout(() => {
                  focusNewAttribute();
                }, 50);
                return;
              }
            }

            focusNewAttribute();

            function focusNewAttribute() {
              // First try to find by data-attribute-id
              const inputElement = document.querySelector(
                `input[data-attribute-id="${lastAttribute.id}"]`
              ) as HTMLInputElement;

              if (inputElement) {
                inputElement.focus();
                onAttributeAutoFocus?.(lastAttribute.id);
              } else {
                // Fallback: focus the last input in the container
                const container = containerRef.current;
                if (container) {
                  const allInputs = container.querySelectorAll('input');
                  const lastInput = allInputs[
                    allInputs.length - 1
                  ] as HTMLInputElement;
                  if (lastInput) {
                    lastInput.focus();
                    // Extract the ID from the last input if possible
                    const attributeId = lastInput.dataset.attributeId;
                    if (attributeId) {
                      onAttributeAutoFocus?.(parseInt(attributeId));
                    }
                  }
                }
              }
            }
          }, 150);

          // Auto-scroll to show the new attribute
          setTimeout(() => {
            // Look for the scrollable container using data attribute
            const scrollableContainer = document.querySelector(
              '[data-scroll-container="attributes-list"]'
            ) as HTMLElement;

            if (scrollableContainer) {
              // Find the specific attribute container
              const attributeContainer = document.querySelector(
                `[data-attribute-container="${lastAttribute.id}"]`
              ) as HTMLElement;

              if (attributeContainer) {
                attributeContainer.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                });
              } else {
                // Fallback: scroll to bottom of container
                scrollableContainer.scrollTo({
                  top: scrollableContainer.scrollHeight,
                  behavior: 'smooth',
                });
              }
            }
          }, 200);

          // Clear the ref after processing
          lastAttributeIdRef.current = null;
        }
      }
    }, [attributes, onAttributeAutoFocus]);

    // Handler for drag end
    const handleDragEnd = useCallback(
      (result: any) => {
        const { source, destination } = result;

        if (!destination) return;

        if (destination.index === source.index) return;

        // Use flushSync to ensure immediate DOM updates and avoid visual glitches
        flushSync(() => {
          onAttributeReorder(source.index, destination.index);
        });
      },
      [onAttributeReorder]
    );

    // Handler for add attribute with auto-focus and scroll
    const handleAddAttribute = useCallback(() => {
      const result = onAttributeAdd();
      // Store the new attribute ID for auto-focus/scroll
      lastAttributeIdRef.current = result.newAttributeId;
    }, [onAttributeAdd]);

    const isNoAttributesGenerated = attributes.length === 0;
    const noAttributesGeneratedBanner = isNoAttributesGenerated ? (
      <Box p="0 15.5px">
        <Flex
          p="12px 14px"
          bg="secondary-25"
          borderRadius="6px"
          gap="6px"
          flexDir="column"
        >
          <KText variant="heading_4">{NO_ATTRIBUTES_BANNER.title}</KText>
          <KText>{NO_ATTRIBUTES_BANNER.subtitle}</KText>
        </Flex>
      </Box>
    ) : null;

    const isLessThanRequired =
      attributes.length < MIN_ATTRIBUTES_REQUIRED && !isNoAttributesGenerated;

    const lessThanRequiredBanner = isLessThanRequired ? (
      <Box p="0 15.5px">
        <Flex p="12px 14px" bg="pastel-6-light" borderRadius="6px">
          <KText color="pastel-6-dark">
            <>
              <KText
                color="pastel-6-dark"
                variant="heading_4"
                fontWeight={700}
                as="span"
              >
                Note:{' '}
              </KText>
              {INSUFFICIENT_ATTRIBUTES_BANNER.getMessage(
                attributes.length,
                MIN_ATTRIBUTES_REQUIRED,
                !generatedAttributes || generatedAttributes.length === 0
              )}
            </>
          </KText>
        </Flex>
      </Box>
    ) : null;

    const renderAttributesList = () => {
      const sortedAttributes = Array.isArray(attributes)
        ? [...attributes].sort((a, b) => a.position - b.position)
        : [];
      return (
        <Flex
          position="relative"
          flexDirection="column"
          // Padding gives the lifted (dragged) row's border + shadow room.
          // Scrolling is owned by the wrapper in the main render so the
          // auto-reject / rejection-email content below the list scrolls too.
          p="8px 16px"
        >
          {isLoading && (
            <Flex
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              bg="rgba(255,255,255,0.7)"
              zIndex={2}
              alignItems="center"
              justifyContent="center"
              opacity={0.5}
              cursor="not-allowed"
            />
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              droppableId="ai-attributes-list"
              // Render the dragged row as a clone portaled to document.body.
              // The drawer panel mispositions/clips a fixed-position preview
              // left inside it, making the dragged row invisible.
              renderClone={(provided, snapshot, rubric) => (
                <AttributeRow
                  item={sortedAttributes[rubric.source.index]}
                  onDelete={onAttributeDelete}
                  onInputChange={onAttributeChange}
                  onAttributeBlur={onAttributeBlur}
                  isLessThanRequired={
                    attributes.length <= MIN_ATTRIBUTES_REQUIRED
                  }
                  provided={provided}
                  snapshot={snapshot}
                />
              )}
            >
              {(provided, snapshot) => (
                <Flex
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  direction="column"
                  maxW="100%"
                  w="100%"
                  flex={1}
                  // The outer (row-flex) scroll container stretches this to
                  // viewport height; floor it at content height so the dashed
                  // drop border wraps every row, not just the visible ones.
                  minH="fit-content"
                  // No overflow here: the outer data-scroll-container is the
                  // single scroller, so the dragged row's border/shadow isn't
                  // clipped at the row edge.
                  // Grey list bg while dragging — shows through the gap left
                  // at the dragged row's position (same effect as departments).
                  bg={snapshot.isDraggingOver ? 'secondary-50' : 'transparent'}
                  borderRadius="8px"
                  border={
                    snapshot.isDraggingOver
                      ? '2px dashed'
                      : '2px solid transparent'
                  }
                  borderColor={
                    snapshot.isDraggingOver ? 'primary-300' : 'transparent'
                  }
                  transition="all 0.2s ease"
                >
                  {sortedAttributes.map((attribute, idx) => {
                    return (
                      <AiAttributeListItem
                        key={`id-${attribute.id}`}
                        item={attribute}
                        index={idx}
                        onDelete={onAttributeDelete}
                        onInputChange={onAttributeChange}
                        onAttributeBlur={onAttributeBlur}
                        isDragging={snapshot.isDraggingOver}
                        isLessThanRequired={
                          attributes.length <= MIN_ATTRIBUTES_REQUIRED
                        }
                      />
                    );
                  })}
                  <Box>{provided.placeholder}</Box>
                </Flex>
              )}
            </Droppable>
          </DragDropContext>
        </Flex>
      );
    };

    const header = () => (
      <Flex
        flexShrink={0}
        justifyContent="space-between"
        w="100%"
        alignItems="center"
        p="0 15.5px"
      >
        <KText variant="heading_4">
          Candidate attributes (Order by priority)
        </KText>
        <AiSupportAttributes
          triggerElement={
            <KText
              p="4px 8px"
              borderRadius="6px"
              bg="secondary-50"
              color="secondary-700"
              cursor="pointer"
            >
              Supported attribute types
            </KText>
          }
          popoverContentProps={{ mr: '0px', ml: '30px' }}
        />
      </Flex>
    );

    const isMaxReached = attributes.length >= MAX_ATTRIBUTES;

    // Check if any attribute has empty description to prevent multiple empty attributes
    const hasEmptyAttribute = attributes.some(
      (attribute) => attribute.description.trim().length === 0
    );

    const footer = () => (
      <Flex
        flexShrink={0}
        justifyContent="space-between"
        w="100%"
        alignItems="center"
        p="0 15.5px"
      >
        <Tooltip
          label={
            hasEmptyAttribute
              ? 'Fill all attributes before adding a new one'
              : `A maximum of ${MAX_ATTRIBUTES} attributes is supported`
          }
          isDisabled={!isMaxReached && !hasEmptyAttribute}
          hasArrow
          placement="top"
        >
          <Flex
            gap="8px"
            alignItems="center"
            bg="primary-100"
            p="6px 12px"
            borderRadius="6px"
            cursor={
              isMaxReached || isLoading || hasEmptyAttribute
                ? 'not-allowed'
                : 'pointer'
            }
            onClick={
              isMaxReached || isLoading || hasEmptyAttribute
                ? undefined
                : handleAddAttribute
            }
            opacity={isMaxReached || isLoading || hasEmptyAttribute ? 0.5 : 1}
          >
            <Icon as={PlusCircle} color="primary-500" />
            <KText variant="heading_4" color="primary-500">
              Add attribute
            </KText>
          </Flex>
        </Tooltip>

        {/* todo: disable this for now, since no api validation needed for this */}
        {/* <Box
          onClick={isLoading || !isValid ? undefined : onExplainClick}
          opacity={isLoading || !isValid ? 0.5 : 1}
          cursor={isLoading || !isValid ? 'not-allowed' : 'pointer'}
        > */}
        {isNoAttributesGenerated ? null : (
          <AiScoreExplained
            isLoading={isLoading}
            // disabled={!isValid}
            attributes={attributes}
            useLocalFormula
          />
        )}
        {/* </Box> */}
      </Flex>
    );

    // Main render
    return (
      <Flex
        w="100%"
        flexDirection="column"
        gap="16px"
        p="17px 0"
        h="100%"
        ref={containerRef}
      >
        {lessThanRequiredBanner}
        {header()}
        <Flex
          flexDirection="column"
          gap="16px"
          flex="1 1 auto"
          minH={0}
          overflowY="auto"
          data-scroll-container="attributes-list"
        >
          {noAttributesGeneratedBanner}
          {renderAttributesList()}
          {footer()}
          {afterListContent}
        </Flex>
      </Flex>
    );
  }
);

AiAttributeListContainer.displayName = 'AiAttributeListContainer';
